import type { Component } from '../router';
import { HealthPill } from './HealthPill';
import { TopNav, TabBar } from './nav/AppNav';
import { RBAC_CONTEXT_EVENT, can } from '../services/capabilities';
import { getSurface, onSurfaceChange } from '../utils/surface';
import { ensureAiI18nLoaded } from '../i18n';

// isActiveTab moved to the nav model in ; re-exported here so existing
// importers (icons.test) keep their path.
export { isActiveTab } from './nav/navModel';

// AppShell — the app-shell layout primitive: the three regions every
// signed-in screen shares — top navigation, a centred content column, and the
// phone bottom navigation. Theme-aware (everything inherits the root data-theme
// tokens) and auth-aware (the navs filter to what this role + device class can
// reach, ). The shell mounts both navs; nav.css shows the right one for
// the viewport, so there is no device branch here.
// promptSurfaceWanted is the prompt surface's composition rule (FE-033.3):
// the desk surface, and a session holding a capability that reaches an AI
// route — `reports:export` gates POST /api/ai/prompt, `work_orders:create`
// gates the upload. The capability mirror is auth-bound to the session's
// device class, so a tablet ceiling already reads false here without a
// device branch of its own.
export function promptSurfaceWanted(): boolean {
  return getSurface() === 'desk' && (can('reports', 'export') || can('work_orders', 'create'));
}

export class AppShell implements Component {
  private root: HTMLElement | null = null;
  private readonly topNav = new TopNav();
  private readonly tabBar = new TabBar();
  private phonePill: HealthPill | null = null;
  private promptHost: HTMLElement | null = null;
  private prompt: Component | null = null;
  private promptLoading = false;
  private disposeSurface: (() => void) | null = null;

  // Grants are per-company, so switching company changes what belongs in the
  // nav. Re-filtering both surfaces on the mirror's broadcast is what lets a
  // switch land without a full page reload (RBAC-006.7). The prompt surface
  // follows the same broadcast: a company where the role reaches no AI route
  // has no strip.
  private readonly onRbacContext = (): void => {
    this.topNav.refresh();
    this.tabBar.refresh();
    this.syncPrompt();
  };

  constructor(private inner: Component) {}

  mount(host: HTMLElement): void {
    const shell = document.createElement('div');
    shell.className = 'shell';

    // The sticky chrome group: top nav + the prompt band (shell.css).
    const chrome = document.createElement('div');
    chrome.className = 'shell__chrome';
    this.topNav.mount(chrome);
    const promptHost = document.createElement('div');
    promptHost.className = 'shell__prompt';
    chrome.appendChild(promptHost);
    shell.appendChild(chrome);
    this.promptHost = promptHost;

    // Phone-only health chip (the top nav, which carries the desktop pill, is
    // hidden below 768px). It rides its own in-flow row ABOVE the content
    // (issue 865): fixed over the page it covered fields and crowded the title
    // row; in flow it reserves its own space and never overlaps content.
    const phoneHealth = document.createElement('div');
    phoneHealth.className = 'healthpill-phone';
    shell.appendChild(phoneHealth);
    this.phonePill = new HealthPill();
    this.phonePill.mount(phoneHealth);

    const main = document.createElement('main');
    main.className = 'shell__main';
    shell.appendChild(main);

    this.tabBar.mount(shell);

    host.appendChild(shell);
    this.root = shell;
    this.inner.mount(main);

    window.addEventListener(RBAC_CONTEXT_EVENT, this.onRbacContext);
    this.disposeSurface = onSurfaceChange(() => this.syncPrompt());
    this.syncPrompt();
  }

  // syncPrompt mounts or unmounts the prompt surface to match the composition
  // rule. The component, its store and its i18n slice stay out of main.js
  // (the 29 kB budget — importing even the store's dormancy setter here
  // measured +4 kB gz on main): loaded on the first desk shell that wants it,
  // concurrently, and mounted only if the rule still holds when the chunk
  // lands — a swallowed load failure leaves the reserved band empty rather
  // than wedging the shell.
  private syncPrompt(): void {
    const wanted = promptSurfaceWanted();
    if (!wanted) {
      this.prompt?.unmount?.();
      this.prompt = null;
      return;
    }
    if (this.prompt || this.promptLoading) return;
    this.promptLoading = true;
    void Promise.all([ensureAiI18nLoaded().catch(() => undefined), import('./PromptSurface')])
      .then(([, mod]) => {
        this.promptLoading = false;
        if (!this.promptHost || !this.root || !promptSurfaceWanted()) return;
        this.prompt = new mod.PromptSurface({
          canPrompt: () => can('reports', 'export'),
          canIngest: () => can('work_orders', 'create'),
        });
        this.prompt.mount(this.promptHost);
      })
      .catch(() => {
        this.promptLoading = false;
      });
  }

  unmount(): void {
    window.removeEventListener(RBAC_CONTEXT_EVENT, this.onRbacContext);
    this.disposeSurface?.();
    this.disposeSurface = null;
    this.prompt?.unmount?.();
    this.prompt = null;
    this.promptHost = null;
    this.phonePill?.unmount();
    this.phonePill = null;
    this.tabBar.unmount();
    this.topNav.unmount();
    if (this.inner.unmount) this.inner.unmount();
    this.root?.remove();
    this.root = null;
  }
}
